import * as React from "react";
import { Toast, ToastTitle, ToastBody, ToastTrigger, Button, tokens } from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import toastEmitter from "../utils/toastEmitter";
import { getIntl } from "../utils/intl";
import { logMessages } from "../utils/messages";
import { MediaProgressManager } from "./MediaProgressManager";

const dismissButton = <Button icon={<Dismiss20Regular />} size="small" appearance="transparent" />;

const defaultToastBodyStyle = {
  lineHeight: tokens.lineHeightBase300
};

const customToastBodyStyle = {
  ...defaultToastBodyStyle,
  color: tokens.colorNeutralForeground2
};

const toastBodyEllipsisStyle = {
  display: "-webkit-box",
  WebkitLineClamp: 5,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: tokens.lineHeightBase300
};

const DEBUG_NOTIFICATIONS =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug_notifications") === "1";

class NotificationsCenter {
  constructor() {
    this.mediaProgress = new MediaProgressManager(toastEmitter);
  }

  registerNonFancyLoad(elementId, elementName = "item") {
    this.mediaProgress.registerNonFancyLoad(elementId, elementName);
  }

  reportNonFancyLoadOutcome(elementId, wasSkipped = false, isRemoval = false) {
    this.mediaProgress.reportNonFancyLoadOutcome(elementId, wasSkipped, isRemoval);
  }

  push(toastContent, options = {}, update = false) {
    if (DEBUG_NOTIFICATIONS) {
      console.debug("[notifications][center] push", {
        update,
        options,
        hasContent: !!toastContent
      });
    }
    if (update) {
      toastEmitter.emit("updateToast", toastContent, options);
    } else {
      toastEmitter.emit("toast", toastContent, options);
    }
  }

  dismiss(toastId) {
    if (DEBUG_NOTIFICATIONS) {
      console.debug("[notifications][center] dismiss", { toastId });
    }
    toastEmitter.emit("dismissToast", toastId);
  }

  success(title, message, options = {}) {
    const intl = getIntl();
    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title;
    const toastContent = (
      <Toast>
        <ToastTitle intent="success" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody style={customToastBodyStyle}>{message}</ToastBody>}
      </Toast>
    );
    this.push(toastContent, { intent: "success", ...options });
  }

  error(title, message, options = {}) {
    const intl = getIntl();
    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Error";
    const toastContent = (
      <Toast>
        <ToastTitle intent="error" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody style={defaultToastBodyStyle}>{message}</ToastBody>}
      </Toast>
    );
    this.push(toastContent, { intent: "error", ...options });
  }

  info(title, message, options = {}) {
    const intl = getIntl();
    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Info";
    const notificationMessage =
      message && logMessages[message] ? intl.formatMessage(logMessages[message], { defaultMessage: message }) : message;
    const toastContent = (
      <Toast>
        <ToastTitle style={{ display: "block" }} intent="info" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody style={customToastBodyStyle}>{notificationMessage}</ToastBody>}
      </Toast>
    );
    this.push(toastContent, { intent: "info", ...options });
  }

  warning(title, message, options = {}) {
    const intl = getIntl();
    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Warning";
    const notificationMessage =
      message && logMessages[message] ? intl.formatMessage(logMessages[message], { defaultMessage: message }) : message;
    const toastContent = (
      <Toast>
        <ToastTitle intent="warning" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {notificationMessage && <ToastBody style={defaultToastBodyStyle}>{notificationMessage}</ToastBody>}
      </Toast>
    );
    this.push(toastContent, { intent: "warning", ...options });
  }

  withMedia(media, title, message, options = {}) {
    const intl = getIntl();
    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Info";
    const toastContent = (
      <Toast className="toast-container">
        <ToastTitle media={media} action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody style={toastBodyEllipsisStyle}>{message}</ToastBody>}
      </Toast>
    );
    this.push(toastContent, { ...options });
  }

  dismissAll() {
    if (DEBUG_NOTIFICATIONS) {
      console.debug("[notifications][center] dismissAll");
    }
    toastEmitter.emit("dismissAll");
    if (this.mediaProgress && typeof this.mediaProgress.resetProgress === "function") {
      this.mediaProgress.resetProgress();
    }
  }
}

const notificationsCenter = new NotificationsCenter();
window.notificationsCenter = notificationsCenter;
export default notificationsCenter;
